import React, { useEffect, useState } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import PasswordStrength, { validatePassword } from '../components/PasswordStrength';

const BRANCHES = ['Head Office','Otun-Ekiti Meeting Point','Ado-Ekiti Meeting Point','Isan-Ekiti Branch','Igede-Ekiti Meeting Point','Ijero-Ekiti Meeting Point','Ilupeju-Ekiti Branch'];
const ROLES = [
  { value:'care_rep',       label:'Customer Care Rep' },
  { value:'ict_staff',      label:'ICT Staff' },
  { value:'ict_manager',    label:'ICT Manager' },
  { value:'branch_manager', label:'Branch Manager' },
  { value:'finance_officer',label:'Finance Officer' },
  { value:'sa_approver',    label:'SA Approver' },
  { value:'sa_initiator',   label:'SA Initiator' },
  { value:'super_admin',    label:'Super Admin' },
];
const ROLE_LABELS = Object.fromEntries(ROLES.map(r => [r.value, r.label]));

const defaultForm = {
  full_name: '', email: '', password: '',
  role: 'care_rep', branch: 'Head Office',
  is_active: true, assigned_approver_id: '',
};

export default function Users() {
  const [users, setUsers]               = useState([]);
  const [saApprovers, setSaApprovers]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);
  const [editUser, setEditUser]         = useState(null);
  const [form, setForm]                 = useState(defaultForm);
  const [saving, setSaving]             = useState(false);
  const [showResetModal, setShowResetModal] = useState(null);
  const [newPassword, setNewPassword]   = useState('');
  const [resetting, setResetting]       = useState(false);

  // Filters
  const [search, setSearch]     = useState('');
  const [filterRole, setFilterRole]     = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = async () => {
    try {
      const [usersRes, approversRes] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/users/sa-approvers').catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setSaApprovers(approversRes.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd  = () => { setEditUser(null); setForm(defaultForm); setShowModal(true); };
  const openEdit = u => {
    setEditUser(u);
    setForm({ full_name:u.full_name, email:u.email, password:'', role:u.role, branch:u.branch, is_active:u.is_active, assigned_approver_id:u.assigned_approver_id || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.full_name || !form.email || (!editUser && !form.password) || !form.role || !form.branch)
      return toast.error('Please fill all required fields');
    if (!editUser) {
      const pwError = validatePassword(form.password);
      if (pwError) return toast.error(pwError);
    }
    if (form.role === 'sa_initiator' && !form.assigned_approver_id)
      return toast.error('Please assign an approver for this SA Initiator');
    setSaving(true);
    try {
      if (editUser) { await api.put(`/api/users/${editUser.id}`, form); toast.success('User updated!'); }
      else          { await api.post('/api/users', form); toast.success('User created! Welcome email sent.'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.full_name}? This cannot be undone.`)) return;
    try { await api.delete(`/api/users/${u.id}`); toast.success('User deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleResetPassword = async () => {
    const pwError = validatePassword(newPassword);
    if (pwError) return toast.error(pwError);
    setResetting(true);
    try {
      await api.post(`/api/users/${showResetModal.id}/reset-password`, { new_password: newPassword });
      toast.success('Password reset! User must change on next login.');
      setShowResetModal(null); setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setResetting(false); }
  };

  const clearFilters = () => { setSearch(''); setFilterRole(''); setFilterBranch(''); setFilterStatus(''); };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.full_name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole   = !filterRole   || u.role === filterRole;
    const matchBranch = !filterBranch || u.branch === filterBranch;
    const matchStatus = !filterStatus || (filterStatus === 'active' ? u.is_active : !u.is_active);
    return matchSearch && matchRole && matchBranch && matchStatus;
  });

  const hasFilters = search || filterRole || filterBranch || filterStatus;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <div style={{ fontFamily:'Sora,sans-serif', fontSize:20, fontWeight:700 }}>👥 User Management</div>
          <div style={{ fontSize:12, color:'var(--text3)', marginTop:3 }}>
            {filtered.length} of {users.length} user{users.length !== 1 ? 's' : ''}
            {hasFilters ? ' (filtered)' : ''}
          </div>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>➕ Add User</button>
      </div>

      {/* Summary stat chips */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14 }}>
        {[
          { label:'Total', count: users.length, color:'var(--sky)', bg:'var(--sky-light)' },
          { label:'Active', count: users.filter(u => u.is_active).length, color:'#0FA86A', bg:'#E6F7F1' },
          { label:'Inactive', count: users.filter(u => !u.is_active).length, color:'#D63A3A', bg:'#FDEAEA' },
          { label:'Must Change PW', count: users.filter(u => u.must_change_password).length, color:'#E09A10', bg:'#FEF6E0' },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, color:s.color, padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:600 }}>
            {s.label}: {s.count}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background:'var(--white)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10 }}>
          <input className="form-input" placeholder="🔍 Search name or email…"
            value={search} onChange={e => setSearch(e.target.value)} />

          <select className="form-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            {ROLES.map(r => (
              <option key={r.value} value={r.value}>
                {r.label} ({users.filter(u => u.role === r.value).length})
              </option>
            ))}
          </select>

          <select className="form-select" value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
            <option value="">All Branches</option>
            {BRANCHES.map(b => (
              <option key={b} value={b}>
                {b} ({users.filter(u => u.branch === b).length})
              </option>
            ))}
          </select>

          <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="active">Active ({users.filter(u => u.is_active).length})</option>
            <option value="inactive">Inactive ({users.filter(u => !u.is_active).length})</option>
          </select>
        </div>

        {hasFilters && (
          <div style={{ marginTop:10, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:12, color:'var(--text3)' }}>
              Showing {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={clearFilters}>✕ Clear filters</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="table-wrap">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'var(--surface)', borderBottom:'2px solid var(--border)' }}>
              {['Name','Email','Role','Branch','Status','Actions'].map(h => (
                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:600, color:'var(--text3)' }}>{h.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ padding:30, textAlign:'center' }}>Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>
                <div style={{ fontSize:28, marginBottom:8 }}>👥</div>
                No users match the current filters
              </td></tr>
            )}
            {!loading && filtered.map(u => (
              <tr key={u.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ fontSize:13, fontWeight:500 }}>{u.full_name}</div>
                  {u.must_change_password && <span style={{ fontSize:10, color:'var(--warn)' }}>⚠ Must change password</span>}
                </td>
                <td style={{ padding:'10px 14px', fontSize:12, color:'var(--text2)' }}>{u.email}</td>
                <td style={{ padding:'10px 14px' }}>
                  <span style={{ fontSize:11, background:'var(--sky-light)', color:'var(--sky2)', padding:'2px 8px', borderRadius:10, fontWeight:500 }}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td style={{ padding:'10px 14px', fontSize:12, color:'var(--text2)' }}>{u.branch}</td>
                <td style={{ padding:'10px 14px' }}>
                  <span style={{ fontSize:11, background: u.is_active ? '#E6F7F1' : '#FDEAEA', color: u.is_active ? '#0FA86A' : '#D63A3A', padding:'2px 8px', borderRadius:10, fontWeight:500 }}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}>✏️ Edit</button>
                    <button className="btn btn-ghost btn-sm" style={{ color:'var(--warn)' }} onClick={() => { setShowResetModal(u); setNewPassword(''); }}>🔑 Reset Pw</button>
                    <button className="btn btn-ghost btn-sm" style={{ color:'var(--red)' }} onClick={() => handleDelete(u)}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:'100%', maxWidth:480, maxHeight:'90vh', overflowY:'auto', boxShadow:'var(--shadow-lg)' }}>
            <div style={{ fontFamily:'Sora,sans-serif', fontSize:15, fontWeight:700, marginBottom:16 }}>
              {editUser ? '✏️ Edit User' : '➕ Add New User'}
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Full Name <span className="req">*</span></label>
                <input className="form-input" value={form.full_name} onChange={e => setForm({...form, full_name:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="req">*</span></label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} disabled={!!editUser} />
              </div>
            </div>
            {!editUser && (
              <div className="form-group">
                <label className="form-label">Temporary Password <span className="req">*</span></label>
                <input className="form-input" type="text" placeholder="User must change on first login"
                  value={form.password} onChange={e => setForm({...form, password:e.target.value})} />
                <PasswordStrength password={form.password} />
              </div>
            )}
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Role <span className="req">*</span></label>
                <select className="form-select" value={form.role} onChange={e => setForm({...form, role:e.target.value, assigned_approver_id:''})}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Branch <span className="req">*</span></label>
                <select className="form-select" value={form.branch} onChange={e => setForm({...form, branch:e.target.value})}>
                  {BRANCHES.map(b => <option key={b}>{b}</option>)}
                </select>
              </div>
            </div>

            {form.role === 'sa_initiator' && (
              <div className="form-group">
                <label className="form-label">Assigned SA Approver <span className="req">*</span></label>
                <select className="form-select" value={form.assigned_approver_id} onChange={e => setForm({...form, assigned_approver_id:e.target.value})}>
                  <option value="">— Select Approver —</option>
                  {saApprovers.length === 0 && <option disabled>No SA Approvers found — create one first</option>}
                  {saApprovers.map(a => <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>)}
                </select>
                <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>
                  ℹ️ The initiator's requests will be routed to this approver automatically.
                </div>
              </div>
            )}

            {editUser && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.is_active ? 'true' : 'false'} onChange={e => setForm({...form, is_active: e.target.value === 'true'})}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            )}

            <div style={{ display:'flex', gap:10, marginTop:16 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ flex:1 }}>
                {saving ? 'Saving…' : editUser ? '💾 Save Changes' : '✅ Create User'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowModal(false)} style={{ flex:1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'var(--white)', borderRadius:'var(--radius)', padding:24, width:'100%', maxWidth:360, boxShadow:'var(--shadow-lg)' }}>
            <div style={{ fontFamily:'Sora,sans-serif', fontSize:15, fontWeight:700, marginBottom:6 }}>🔑 Reset Password</div>
            <div style={{ fontSize:12, color:'var(--text3)', marginBottom:16 }}>
              Resetting password for: <strong>{showResetModal.full_name}</strong>
            </div>
            <div className="form-group">
              <label className="form-label">New Temporary Password</label>
              <input className="form-input" type="text" placeholder="Min 8 chars, uppercase, number"
                value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <PasswordStrength password={newPassword} />
            </div>
            <div style={{ fontSize:11, color:'var(--warn)', marginBottom:14 }}>
              ⚠️ User will be required to change this password on next login.
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary" onClick={handleResetPassword} disabled={resetting} style={{ flex:1 }}>
                {resetting ? 'Resetting…' : '🔑 Reset Password'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowResetModal(null)} style={{ flex:1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
